import React, { useContext, useEffect, useState, useCallback, useRef } from 'react';
import { Warehouse, Refrigerator, Archive, Boxes, Box, Trash2 } from 'lucide-react';
import { UserContext } from '../../../context/UserContext';
import { VENUE_TYPES } from '../../../lib/consts/venueTypes';
import MesasSection from '../ClubEntradasComponents/MesasSection';
import apiFetch, { safeJson, apiErrorDetail } from '../../../context/api';

/**
 * Vista aérea del almacén (Opción C): cada ubicación es una caja flotante
 * colocable en el plano. Arrastra la cabecera para moverla; pulsa ➡ en un
 * producto para traspasarlo a otra ubicación (con báscula si es por peso).
 * Backend: GET /v1/stock/board, PATCH/POST /v1/stock/locations, POST /v1/stock/transfer
 */

const KIND_LABELS = { principal: 'Principal', nevera: 'Nevera', armario: 'Armario', sub_almacen: 'Sub-almacén' };
const KIND_ICON = { principal: Warehouse, nevera: Refrigerator, armario: Archive, sub_almacen: Boxes };
const KIND_BADGE = {
  principal: 'bg-indigo-50 text-indigo-700',
  nevera: 'bg-sky-50 text-sky-700',
  armario: 'bg-amber-50 text-amber-700',
  sub_almacen: 'bg-slate-100 text-slate-600',
};
const BOX_W = 230;
const BOX_H = 200; // alto estimado de caja (para fit / límites)

const StockBoard = () => {
  const [token, , , venueID, , , , , , , , venueType] = useContext(UserContext);
  const [locations, setLocations] = useState([]);
  const [products, setProducts] = useState([]);
  const [positions, setPositions] = useState({}); // { locId: {x,y} }
  const [sizes, setSizes] = useState({}); // { locId: {w,h} }
  const resizeRef = useRef(null); // { id, startX, startY, baseW, baseH }
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [transfer, setTransfer] = useState(null); // { product, fromLoc }
  const [showAdd, setShowAdd] = useState(false);
  const [zone, setZone] = useState('barra'); // barra | dispensario
  const [boxCat, setBoxCat] = useState({}); // { locId: categoría activa en esa caja }
  const [cardDrag, setCardDrag] = useState(null); // { product, fromLoc, x, y, overId } (ghost)
  const cardDragRef = useRef(null); // { product, fromLoc, startX, startY, active, overId }
  const floorRef = useRef(null);
  const dragRef = useRef(null); // { id, startX, startY, baseX, baseY }
  const [view, setView] = useState({ x: 0, y: 0, z: 1 }); // pan + zoom del lienzo
  const panRef = useRef(null); // { startX, startY, baseX, baseY }
  const pointersRef = useRef(new Map()); // pointerId -> {x,y} (para pinch en tablet)
  const pinchRef = useRef(null); // { startDist, baseZ, baseX, baseY, midX, midY }
  const positionsRef = useRef({});
  useEffect(() => { positionsRef.current = positions; }, [positions]);
  const sizesRef = useRef({});
  useEffect(() => { sizesRef.current = sizes; }, [sizes]);

  // bbox del contenido en coords mundo (usa tamaño real de cada caja)
  const contentBBox = () => {
    const ps = positionsRef.current || {};
    const ids = Object.keys(ps);
    if (ids.length === 0) return null;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    ids.forEach((id) => {
      const p = ps[id]; const s = sizesRef.current?.[id] || {};
      const w = s.w || BOX_W; const h = (s.h || BOX_H) + 130; // grid + cabecera/pills/asa
      minX = Math.min(minX, p.x); minY = Math.min(minY, p.y);
      maxX = Math.max(maxX, p.x + w); maxY = Math.max(maxY, p.y + h);
    });
    return { minX, minY, maxX, maxY };
  };

  // Paneo libre con margen amplio: deja pasear ~una pantalla más allá del
  // contenido en cualquier dirección, para poder llevar cualquier caja al centro
  // sin perderse al infinito.
  const clampView = useCallback((v) => {
    const el = floorRef.current;
    const bb = contentBBox();
    if (!el || !bb) return v;
    const vpW = el.clientWidth, vpH = el.clientHeight;
    const buf = Math.max(vpW, vpH); // ~una pantalla de margen libre
    const xMin = -buf - bb.maxX * v.z, xMax = vpW + buf - bb.minX * v.z;
    const yMin = -buf - bb.maxY * v.z, yMax = vpH + buf - bb.minY * v.z;
    return { ...v, x: Math.min(xMax, Math.max(xMin, v.x)), y: Math.min(yMax, Math.max(yMin, v.y)) };
  }, []);

  const fitToView = useCallback(() => {
    const el = floorRef.current;
    const bb = contentBBox();
    if (!el || !bb) { setView({ x: 0, y: 0, z: 1 }); return; }
    const pad = 48;
    const cw = Math.max(1, bb.maxX - bb.minX), ch = Math.max(1, bb.maxY - bb.minY);
    const z = Math.min(2.5, Math.max(0.3, Math.min((el.clientWidth - pad * 2) / cw, (el.clientHeight - pad * 2) / ch, 1)));
    setView({ z, x: (el.clientWidth - cw * z) / 2 - bb.minX * z, y: (el.clientHeight - ch * z) / 2 - bb.minY * z });
  }, []);

  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...(venueID && { 'x-venue-id': venueID }) };
  const authGet = { headers: { Authorization: `Bearer ${token}`, ...(venueID && { 'x-venue-id': venueID }) } };
  // Formato unificado con el resto del software: punto decimal, coma miles (en-GB).
  const fmtN = (n) => Number(n || 0).toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 3 });
  const fmtMoney = (n) => Number(n || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const unitOf = (p) => (p.unit_type === 'weight' ? 'g' : 'u');

  const fetchBoard = useCallback(async () => {
    if (!venueID) return;
    setLoading(true); setError('');
    try {
      const res = await apiFetch(`/v1/stock/board?venue_id=${venueID}`, authGet);
      const data = await safeJson(res);
      if (!res.ok) throw new Error(apiErrorDetail(data, 'Error al cargar el almacén'));
      setLocations(data.locations || []);
      setProducts(data.products || []);
      // posiciones: usa pos_x/pos_y; auto-layout en fila si null
      setPositions((prev) => {
        const next = { ...prev };
        (data.locations || []).forEach((l, i) => {
          if (next[l.id]) return;
          next[l.id] = (l.pos_x != null && l.pos_y != null) ? { x: l.pos_x, y: l.pos_y } : { x: 24 + i * (BOX_W + 20), y: 24 };
        });
        return next;
      });
      setSizes((prev) => {
        const next = { ...prev };
        (data.locations || []).forEach((l) => {
          if (next[l.id]) return;
          next[l.id] = { w: l.pos_w || 280, h: l.pos_h || 240 };
        });
        return next;
      });
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venueID, token]);

  useEffect(() => { fetchBoard(); }, [fetchBoard]);

  // Borrar una ubicación (su stock vuelve a la principal). La principal no se borra.
  const handleDeleteLocation = async (loc) => {
    if (loc.kind === 'principal') return;
    if (!window.confirm(`¿Borrar la ubicación «${loc.name}»? Su stock se devolverá al almacén principal.`)) return;
    try {
      const res = await apiFetch(`/v1/stock/locations/${loc.id}?venue_id=${venueID}`, { method: 'DELETE', headers });
      if (!res.ok) throw new Error(apiErrorDetail(await safeJson(res), 'No se pudo borrar la ubicación'));
      await fetchBoard();
    } catch (e) {
      setError(e.message || 'No se pudo borrar la ubicación');
    }
  };

  // ── Drag de cajas (pointer events, táctil). Delta / zoom = mundo. ──
  const onPointerDownBox = (e, loc) => {
    if (e.target.closest('[data-no-drag]')) return;
    e.stopPropagation(); // no iniciar pan del lienzo
    const pos = positions[loc.id] || { x: 24, y: 24 };
    dragRef.current = { id: loc.id, startX: e.clientX, startY: e.clientY, baseX: pos.x, baseY: pos.y };
  };
  // ── Pan del lienzo (arrastrar el fondo) ──
  const onFloorPointerDown = (e) => {
    // Registrar el puntero para detectar pinch (2 dedos en tablet).
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointersRef.current.size === 2) {
      const [a, b] = [...pointersRef.current.values()];
      pinchRef.current = {
        startDist: Math.hypot(a.x - b.x, a.y - b.y) || 1,
        baseZ: view.z, baseX: view.x, baseY: view.y,
        midX: (a.x + b.x) / 2, midY: (a.y + b.y) / 2,
      };
      panRef.current = null; dragRef.current = null; resizeRef.current = null; cardDragRef.current = null;
      return;
    }
    if (e.target.closest('[data-box]')) return; // si pulsas una caja, no paneo
    panRef.current = { startX: e.clientX, startY: e.clientY, baseX: view.x, baseY: view.y };
  };
  const onResizePointerDown = (e, loc) => {
    e.stopPropagation();
    const s = sizes[loc.id] || { w: 280, h: 240 };
    resizeRef.current = { id: loc.id, startX: e.clientX, startY: e.clientY, baseW: s.w, baseH: s.h };
  };
  // ── Drag de PRODUCTO entre cajas (tap = abrir diálogo; arrastrar = soltar en otra caja) ──
  const onCardPointerDown = (e, product, loc) => {
    e.stopPropagation();
    // Guardar la lista scrollable de la caja para poder hacer scroll manual en tablet
    // (el touch-action:none del board impide el scroll nativo; lo movemos a mano).
    const scrollEl = e.currentTarget.closest('[data-scroll]');
    cardDragRef.current = {
      product, fromLoc: loc, startX: e.clientX, startY: e.clientY,
      active: false, overId: null, mode: null,
      scrollEl: scrollEl || null, baseScrollTop: scrollEl ? scrollEl.scrollTop : 0,
    };
  };
  const onPointerMove = (e) => {
    // Pinch (2 dedos): zoom proporcional a la separación, anclado al punto medio.
    if (pointersRef.current.has(e.pointerId)) pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const pinch = pinchRef.current;
    if (pinch && pointersRef.current.size >= 2) {
      const [a, b] = [...pointersRef.current.values()];
      const dist = Math.hypot(a.x - b.x, a.y - b.y) || 1;
      const ratio = dist / pinch.startDist;
      const rect = floorRef.current?.getBoundingClientRect();
      const cx = pinch.midX - (rect?.left || 0), cy = pinch.midY - (rect?.top || 0);
      setView(() => {
        const z = Math.min(2.5, Math.max(0.3, pinch.baseZ * ratio));
        const wx = (cx - pinch.baseX) / pinch.baseZ, wy = (cy - pinch.baseY) / pinch.baseZ;
        return clampView({ z, x: cx - wx * z, y: cy - wy * z });
      });
      return;
    }
    const cd = cardDragRef.current;
    if (cd) {
      const dx = e.clientX - cd.startX;
      const dy = e.clientY - cd.startY;
      if (cd.mode === null) {
        if (Math.hypot(dx, dy) < 5) return;   // umbral: por debajo es un tap
        // Vertical → scroll de la lista de la caja; horizontal → traspaso de producto.
        cd.mode = (cd.scrollEl && Math.abs(dy) > Math.abs(dx)) ? 'scroll' : 'drag';
      }
      if (cd.mode === 'scroll') {
        cd.scrollEl.scrollTop = cd.baseScrollTop - dy;
        return;
      }
      cd.active = true;
      const box = document.elementFromPoint(e.clientX, e.clientY)?.closest('[data-loc-id]');
      cd.overId = box ? box.getAttribute('data-loc-id') : null;
      setCardDrag({ product: cd.product, fromLoc: cd.fromLoc, x: e.clientX, y: e.clientY, overId: cd.overId });
      return;
    }
    const rz = resizeRef.current;
    if (rz) {
      const w = Math.max(200, rz.baseW + (e.clientX - rz.startX) / view.z);
      const h = Math.max(140, rz.baseH + (e.clientY - rz.startY) / view.z);
      setSizes((s) => ({ ...s, [rz.id]: { w, h } }));
      return;
    }
    const d = dragRef.current;
    if (d) {
      const x = d.baseX + (e.clientX - d.startX) / view.z;
      const y = d.baseY + (e.clientY - d.startY) / view.z;
      setPositions((p) => ({ ...p, [d.id]: { x, y } }));
      return;
    }
    const pan = panRef.current;
    if (pan) {
      setView((v) => clampView({ ...v, x: pan.baseX + (e.clientX - pan.startX), y: pan.baseY + (e.clientY - pan.startY) }));
    }
  };
  const onPointerUp = async (e) => {
    // Soltar un dedo: quitarlo del registro y cerrar el pinch si quedan <2.
    if (e && e.pointerId != null) pointersRef.current.delete(e.pointerId);
    if (pointersRef.current.size < 2) pinchRef.current = null;
    const cd = cardDragRef.current;
    if (cd) {
      cardDragRef.current = null;
      setCardDrag(null);
      if (cd.mode === 'scroll') return;                                // fue scroll de la lista
      const { active, overId, product, fromLoc } = cd;
      if (!active) { setTransfer({ product, fromLoc }); return; }       // tap → diálogo
      if (overId && overId !== fromLoc.id) {                            // soltado en otra caja
        setTransfer({ product, fromLoc, presetTo: overId });
      }
      return;
    }
    const rz = resizeRef.current;
    if (rz) {
      resizeRef.current = null;
      const s = sizes[rz.id];
      if (s) {
        try {
          await apiFetch(`/v1/stock/locations/${rz.id}?venue_id=${venueID}`, {
            method: 'PATCH', headers, body: JSON.stringify({ pos_w: Math.round(s.w), pos_h: Math.round(s.h) }),
          });
        } catch { /* tamaño no crítico */ }
      }
      return;
    }
    const d = dragRef.current;
    if (d) {
      dragRef.current = null;
      const pos = positions[d.id];
      if (pos) {
        try {
          await apiFetch(`/v1/stock/locations/${d.id}?venue_id=${venueID}`, {
            method: 'PATCH', headers, body: JSON.stringify({ pos_x: Math.round(pos.x), pos_y: Math.round(pos.y) }),
          });
        } catch { /* posición no crítica */ }
      }
      return;
    }
    panRef.current = null;
  };
  // Cancelación del puntero (p.ej. el navegador toma el gesto): limpiar estado.
  const onPointerCancel = (e) => {
    if (e && e.pointerId != null) pointersRef.current.delete(e.pointerId);
    if (pointersRef.current.size < 2) pinchRef.current = null;
    cardDragRef.current = null; dragRef.current = null; resizeRef.current = null; panRef.current = null;
    setCardDrag(null);
  };
  // ── Zoom al cursor (rueda / trackpad) ──
  const applyZoom = useCallback((deltaY, clientX, clientY) => {
    const rect = floorRef.current?.getBoundingClientRect();
    if (!rect) return;
    const cx = clientX - rect.left, cy = clientY - rect.top;
    setView((v) => {
      const z = Math.min(2.5, Math.max(0.3, v.z * (deltaY < 0 ? 1.1 : 1 / 1.1)));
      const wx = (cx - v.x) / v.z, wy = (cy - v.y) / v.z; // punto mundo bajo el cursor
      return clampView({ z, x: cx - wx * z, y: cy - wy * z }); // anclar ese punto + límites
    });
  }, [clampView]);
  // wheel con listener no-pasivo (para poder preventDefault). Distingue ratón vs trackpad:
  // - ctrlKey → PINCH del trackpad (o ctrl+rueda) → zoom al cursor.
  // - rueda de ratón (deltaY grande y discreto, sin deltaX) → zoom (comportamiento de siempre).
  // - trackpad dos dedos (deltas pequeños y/o con deltaX) → PAN del lienzo.
  //   Si el gesto cae sobre la lista de una caja que puede scrollear, se deja el scroll nativo.
  // La tablet no pasa por aquí (usa pointer events / pinchRef), así que no se toca.
  useEffect(() => {
    const el = floorRef.current;
    if (!el) return;
    const handler = (e) => {
      if (e.ctrlKey) { // pinch trackpad / ctrl+rueda → zoom
        e.preventDefault();
        applyZoom(e.deltaY, e.clientX, e.clientY);
        return;
      }
      const isMouseWheel = e.deltaX === 0 && Math.abs(e.deltaY) >= 40;
      if (isMouseWheel) { // rueda de ratón → zoom (igual que antes)
        e.preventDefault();
        applyZoom(e.deltaY, e.clientX, e.clientY);
        return;
      }
      // Trackpad dos dedos: si estamos sobre una lista scrollable, dejar el scroll nativo.
      const list = e.target?.closest?.('[data-scroll]');
      if (list && list.scrollHeight > list.clientHeight) return;
      // si no, PAN del lienzo.
      e.preventDefault();
      setView((v) => clampView({ ...v, x: v.x - e.deltaX, y: v.y - e.deltaY }));
    };
    el.addEventListener('wheel', handler, { passive: false });
    return () => el.removeEventListener('wheel', handler);
  }, [applyZoom, clampView]);

  const addLocation = async (name, kind) => {
    setError('');
    try {
      const res = await apiFetch('/v1/stock/locations', { method: 'POST', headers, body: JSON.stringify({ venue_id: venueID, name, kind, is_sales_source: false }) });
      if (!res.ok) throw new Error(apiErrorDetail(await safeJson(res), 'Error al crear'));
      setShowAdd(false);
      await fetchBoard();
    } catch (e) { setError(e.message); }
  };

  // productos de la zona activa (Barra = no dispensario)
  const inZone = (p) => (zone === 'dispensario' ? !!p.is_dispensario : !p.is_dispensario);
  const catOf = (p) => p.category || 'Sin categoría';
  // productos de la zona presentes en una ubicación (con stock ≠ 0, incluidos NEGATIVOS).
  // Los negativos se muestran (en rojo) para que el staff sepa qué hay que reponer:
  // staff sin estrella puede vender hasta negativo pero no rellenar.
  // Mostramos DISPONIBLE (físico − carritos abiertos): al añadir al carrito el producto ya
  // sale de la balda, así que Ubicaciones refleja lo que queda. Fallback al físico si el BE
  // aún no envía available_by_location.
  const productsIn = (locId) => products
    .filter(inZone)
    .map((p) => ({ p, qty: Number((p.available_by_location ?? p.by_location)?.[locId] || 0) }))
    .filter((x) => x.qty !== 0)
    .sort((a, b) => {
      const an = a.qty < 0, bn = b.qty < 0;
      if (an !== bn) return an ? -1 : 1;   // negativos primero
      return a.p.name.localeCompare(b.p.name);
    });

  // Todas las posiciones en negativo de la zona activa (para el aviso superior).
  const locName = {};
  locations.forEach((l) => { locName[l.id] = l.name; });
  const negatives = [];
  products.filter(inZone).forEach((p) => {
    Object.entries(p.available_by_location ?? p.by_location ?? {}).forEach(([locId, q]) => {
      const qn = Number(q || 0);
      if (qn < 0) negatives.push({ p, locId, qty: qn });
    });
  });
  negatives.sort((a, b) => a.qty - b.qty);

  return (
    <div className="p-4 sm:p-6">
      {/* Club social (CRUO): las mesas se gestionan aquí, junto al resto de ubicaciones.
          Entidad aparte (service_tables) — el board de stock ni se entera. */}
      {venueType === VENUE_TYPES.SOCIAL_CLUB && (
        <div className="mb-4">
          <MesasSection />
        </div>
      )}
      <div className="mb-3 flex items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-[#0A0A0A]">Ubicaciones</h2>
          <p className="text-sm text-[#737373]">Vista aérea · arrastra el fondo para mover, rueda para zoom, ⠿ para mover una caja.</p>
        </div>
        <button onClick={() => setShowAdd(true)} className="rounded-lg bg-[#171717] px-4 py-2.5 text-sm font-semibold text-white hover:bg-black">+ Ubicación</button>
      </div>

      {/* Pestañas de zona (Barra / Dispensario) */}
      <div className="mb-4 inline-flex gap-1 rounded-xl bg-[#F5F5F5] p-1">
        {(venueType === VENUE_TYPES.SOCIAL_CLUB
          ? [['barra', 'Barra']]
          : [['barra', 'Barra'], ['dispensario', 'Dispensario']]
        ).map(([k, label]) => (
          <button
            key={k}
            onClick={() => setZone(k)}
            className={`rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${zone === k ? 'bg-white text-[#0A0A0A] shadow-sm' : 'text-[#737373] hover:text-[#0A0A0A]'}`}
          >
            {label}
          </button>
        ))}
      </div>

      {error && <p className="mb-3 rounded-md bg-red-50 px-3 py-2 text-sm text-[#DC2626]">{error}</p>}
      {loading && <p className="mb-3 text-sm text-[#737373]">Cargando…</p>}

      {/* Aviso de stock en negativo (vendido por debajo de 0): pendiente de reponer. */}
      {negatives.length > 0 && (
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-3">
          <div className="flex items-center gap-2 text-sm font-semibold text-[#DC2626]">
            ⚠️ {negatives.length} {negatives.length === 1 ? 'producto en negativo' : 'productos en negativo'} · pendientes de reponer
          </div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {negatives.map(({ p, locId, qty }) => (
              <span key={`${p.id}-${locId}`} className="inline-flex items-center gap-1 rounded-md border border-red-200 bg-white px-2 py-0.5 text-[11px] text-[#0A0A0A]">
                <span className="font-medium">{p.name}</span>
                <span className="text-[#737373]">· {locName[locId] || 'Ubicación'}</span>
                <span className="font-bold tabular-nums text-[#DC2626]">{fmtN(qty)}{unitOf(p)}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      <div
        ref={floorRef}
        onPointerDown={onFloorPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerLeave={onPointerUp}
        onPointerCancel={onPointerCancel}
        className="relative w-full select-none overflow-hidden rounded-2xl border-2 border-dashed border-[#E5E5E5]"
        style={{
          height: 'calc(100vh - 280px)', minHeight: 420,
          cursor: panRef.current ? 'grabbing' : 'grab', touchAction: 'none',
          backgroundImage: 'linear-gradient(#f0f0f0 1px, transparent 1px), linear-gradient(90deg, #f0f0f0 1px, transparent 1px)',
          backgroundSize: `${28 * view.z}px ${28 * view.z}px`,
          backgroundPosition: `${view.x}px ${view.y}px`,
        }}
      >
        {/* Capa "mundo": pan + zoom. Las cajas viven aquí en coords mundo. */}
        <div style={{ position: 'absolute', inset: 0, transform: `translate(${view.x}px, ${view.y}px) scale(${view.z})`, transformOrigin: '0 0' }}>
        {locations.map((loc) => {
          const pos = positions[loc.id] || { x: 24, y: 24 };
          const size = sizes[loc.id] || { w: 280, h: 240 };
          const items = productsIn(loc.id);
          const sumQty = items.reduce((s, { qty }) => s + qty, 0);
          const value = items.reduce((s, { p, qty }) => s + qty * (Number(p.price) || 0), 0);
          const boxCats = Array.from(new Set(items.map(({ p }) => catOf(p)))).sort((a, b) => a.localeCompare(b));
          const activeCat = boxCat[loc.id] || 'all';
          const shown = activeCat === 'all' ? items : items.filter(({ p }) => catOf(p) === activeCat);
          return (
            <div
              key={loc.id}
              data-box
              data-loc-id={loc.id}
              className={`absolute rounded-xl border bg-white shadow-md border-[#E5E5E5] ${cardDrag && cardDrag.overId === loc.id && cardDrag.fromLoc.id !== loc.id ? 'ring-2 ring-lime-400 ring-offset-1' : ''}`}
              style={{ left: pos.x, top: pos.y, width: size.w, cursor: 'default' }}
            >
              {/* Asa de arrastre (mover la caja) */}
              <div
                onPointerDown={(e) => onPointerDownBox(e, loc)}
                className="flex items-center justify-center rounded-t-xl bg-[#F7F7F3] py-1 text-[#B8B8B8] hover:bg-[#EFEFEF]"
                style={{ cursor: 'default', touchAction: 'none' }}
                title="Arrastrar para mover"
              >
                <span className="text-base leading-none tracking-widest select-none">⠿</span>
              </div>
              <div className="p-3">
                <div className="mb-2 flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex min-w-0 items-center gap-1.5">
                      {(() => { const KindIcon = KIND_ICON[loc.kind] || Box; return <KindIcon size={15} className="flex-shrink-0 text-[#737373]" />; })()}
                      <div className="truncate text-sm font-semibold text-[#0A0A0A]">{loc.name}</div>
                    </div>
                    <div className="mt-0.5 flex flex-wrap items-center gap-1">
                      <span className={`inline-flex rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${KIND_BADGE[loc.kind] || KIND_BADGE.sub_almacen}`}>{KIND_LABELS[loc.kind] || loc.kind}</span>
                    </div>
                    <div className="mt-1 text-[11px] text-[#737373]">{items.length} prod · {fmtN(sumQty)} ud · €{fmtMoney(value)}</div>
                  </div>
                  {loc.kind !== 'principal' && (
                    <button
                      data-no-drag
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={(e) => { e.stopPropagation(); handleDeleteLocation(loc); }}
                      title="Borrar ubicación (el stock vuelve a la principal)"
                      className="flex-shrink-0 rounded-md p-1 text-[#A3A3A3] hover:bg-red-50 hover:text-red-600 transition-colors"
                    >
                      <Trash2 size={15} />
                    </button>
                  )}
                </div>
                {/* Pills de categoría (solo las presentes en esta caja). Wrap si no caben. */}
                {boxCats.length > 0 && (
                  <div className="mb-2 flex flex-wrap gap-1" data-no-drag>
                    <button onClick={() => setBoxCat((m) => ({ ...m, [loc.id]: 'all' }))} className={`rounded-full px-2 py-0.5 text-[10px] font-semibold transition-colors ${activeCat === 'all' ? 'bg-[#171717] text-white' : 'bg-[#F0F0F0] text-[#737373] hover:bg-[#E5E5E5]'}`}>Todas</button>
                    {boxCats.map((c) => (
                      <button key={c} onClick={() => setBoxCat((m) => ({ ...m, [loc.id]: c }))} className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide transition-colors ${activeCat === c ? 'bg-[#171717] text-white' : 'bg-[#F0F0F0] text-[#737373] hover:bg-[#E5E5E5]'}`}>{c}</button>
                    ))}
                  </div>
                )}
                <div
                  className="grid gap-1.5 overflow-y-auto"
                  data-no-drag
                  data-scroll
                  style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(118px, 1fr))', maxHeight: size.h }}
                >
                  {shown.length === 0 && <p className="col-span-full py-2 text-center text-xs text-[#A3A3A3]">Vacío</p>}
                  {shown.map(({ p, qty }) => {
                    const neg = qty < 0;                                 // en negativo: pendiente de reponer
                    const low = !neg && !p.is_dispensario && qty <= 5;   // umbral simple (Barra)
                    return (
                      <div
                        key={p.id}
                        data-no-drag
                        onPointerDown={(e) => onCardPointerDown(e, p, loc)}
                        className={`rounded-lg border p-2 hover:bg-white ${neg ? 'border-red-200 bg-red-50 hover:border-red-300' : 'border-[#EEE] bg-[#FAFAFA] hover:border-lime-300'}`}
                        style={{ cursor: 'default', touchAction: 'none' }}
                        title={neg ? `${p.name} · en negativo, pendiente de reponer` : 'Toca para traspasar · arrastra a otra caja'}
                      >
                        <div className="truncate text-[11px] font-semibold text-[#0A0A0A]" title={p.name}>{p.name}</div>
                        <div className="mt-0.5 flex items-center gap-1">
                          <span className={`text-sm font-bold tabular-nums ${neg ? 'text-[#DC2626]' : low ? 'text-amber-600' : 'text-[#0A0A0A]'}`}>{fmtN(qty)}{unitOf(p)}</span>
                          {neg && <span className="rounded bg-red-100 px-1 py-0.5 text-[8px] font-bold uppercase tracking-wide text-[#DC2626]">Reponer</span>}
                        </div>
                        <div className="mt-0.5 text-[9px] text-[#A3A3A3]">tot {fmtN(p.total)}{unitOf(p)}{p.price != null ? ` · ${fmtMoney(p.price)}€` : ''}</div>
                      </div>
                    );
                  })}
                </div>
              </div>{/* /p-3 */}
              {/* Asa de redimensionar */}
              <div
                data-no-drag
                onPointerDown={(e) => onResizePointerDown(e, loc)}
                className="absolute bottom-0.5 right-0.5 select-none text-[#C0C0C0] hover:text-[#737373]"
                style={{ cursor: 'nwse-resize', touchAction: 'none', lineHeight: 1 }}
                title="Redimensionar"
              >◢</div>
            </div>
          );
        })}
        </div>{/* /capa mundo */}

        {locations.length === 0 && !loading && (
          <p className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-sm text-[#A3A3A3]">Sin ubicaciones. Crea una con "+ Ubicación".</p>
        )}

        {/* Controles de zoom */}
        <div className="absolute bottom-3 right-3 flex items-center gap-1 rounded-lg border border-[#E5E5E5] bg-white/90 p-1 shadow-sm backdrop-blur" data-no-drag>
          <button onPointerDown={(e) => e.stopPropagation()} onClick={() => applyZoom(1, floorRef.current.getBoundingClientRect().left + floorRef.current.clientWidth / 2, floorRef.current.getBoundingClientRect().top + floorRef.current.clientHeight / 2)} className="h-7 w-7 rounded text-[#737373] hover:bg-[#F5F5F5]" title="Alejar">−</button>
          <span className="min-w-[44px] text-center text-xs font-semibold tabular-nums text-[#737373]">{Math.round(view.z * 100)}%</span>
          <button onPointerDown={(e) => e.stopPropagation()} onClick={() => applyZoom(-1, floorRef.current.getBoundingClientRect().left + floorRef.current.clientWidth / 2, floorRef.current.getBoundingClientRect().top + floorRef.current.clientHeight / 2)} className="h-7 w-7 rounded text-[#737373] hover:bg-[#F5F5F5]" title="Acercar">+</button>
          <button onPointerDown={(e) => e.stopPropagation()} onClick={fitToView} className="ml-1 rounded px-2 py-1 text-xs font-medium text-[#737373] hover:bg-[#F5F5F5]" title="Encajar todo en la vista">Encajar</button>
          <button onPointerDown={(e) => e.stopPropagation()} onClick={() => setView({ x: 0, y: 0, z: 1 })} className="rounded px-2 py-1 text-xs font-medium text-[#737373] hover:bg-[#F5F5F5]" title="Restablecer">Reset</button>
        </div>
      </div>

      {transfer && (
        <TransferDialog
          transfer={transfer}
          locations={locations}
          token={token}
          venueID={venueID}
          onClose={() => setTransfer(null)}
          onDone={() => { setTransfer(null); fetchBoard(); }}
        />
      )}
      {showAdd && <AddLocationDialog onClose={() => setShowAdd(false)} onCreate={addLocation} />}

      {/* Ghost que sigue al cursor al arrastrar un producto */}
      {cardDrag && cardDrag.x != null && (
        <div
          className="pointer-events-none fixed z-[60] rounded-lg border border-lime-400 bg-white px-2 py-1 text-xs font-semibold text-[#0A0A0A] shadow-lg"
          style={{ left: cardDrag.x + 10, top: cardDrag.y + 10 }}
        >
          {cardDrag.product.name} →
        </div>
      )}
    </div>
  );
};

// ── Diálogo de traspaso (con báscula si el producto es por peso) ──
const TransferDialog = ({ transfer, locations, token, venueID, onClose, onDone }) => {
  const { product, fromLoc } = transfer;
  const isWeight = product.unit_type === 'weight';
  const unit = isWeight ? 'g' : 'u';
  const step = isWeight ? '0.001' : '1';
  const available = Number(product.by_location?.[fromLoc.id] || 0);
  const fmt = (n) => Number(n || 0).toLocaleString('en-GB', { maximumFractionDigits: 3 });

  // Tara por ubicación: la de esa ubicación si existe, si no la del producto, si no ''.
  const tareForLoc = (locId) => {
    const t = product.tare_by_location?.[locId];
    if (t != null) return String(t);
    return product.tare_grams != null ? String(product.tare_grams) : '';
  };
  const sourceTara = Number(tareForLoc(fromLoc.id) || 0);

  const [toLoc, setToLoc] = useState(transfer.presetTo || '');
  const [qty, setQty] = useState('');
  const [gross, setGross] = useState('');
  const [taraInput, setTaraInput] = useState(transfer.presetTo ? tareForLoc(transfer.presetTo) : '');
  const [taraTouched, setTaraTouched] = useState(false);   // el usuario editó la tara a mano
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  // Al cambiar el destino, pre-rellena su tara guardada (o la del producto), salvo que
  // el usuario ya la haya editado a mano (entonces se respeta lo que escribió).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (!taraTouched) setTaraInput(toLoc ? tareForLoc(toLoc) : ''); }, [toLoc]);

  const taraVal = parseFloat(taraInput) || 0;
  const byWeight = isWeight && gross !== '';
  const net = byWeight ? (parseFloat(gross) - taraVal) : (qty !== '' ? parseFloat(qty) : null);

  const submit = async () => {
    setErr('');
    if (!toLoc) { setErr('Elige destino'); return; }
    if (net == null || isNaN(net) || net <= 0) { setErr('Cantidad no válida'); return; }
    if (net > available) { setErr(`Solo hay ${fmt(available)}${unit} en ${fromLoc.name}`); return; }
    setBusy(true);
    try {
      const body = { product_id: product.id, venue_id: venueID, from_location_id: fromLoc.id, to_location_id: toLoc, quantity: net };
      if (isWeight && taraInput !== '') body.dest_tare_grams = taraVal;   // guarda la tara del destino
      const res = await apiFetch('/v1/stock/transfer', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...(venueID && { 'x-venue-id': venueID }) },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(apiErrorDetail(await safeJson(res), 'Error en el traspaso'));
      onDone();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between border-b border-[#E5E5E5] p-4">
          <div>
            <h3 className="text-base font-semibold text-[#0A0A0A]">Traspasar · {product.name}</h3>
            <p className="mt-0.5 text-sm text-[#737373]">
              Desde <strong>{fromLoc.name}</strong> · disponible {fmt(available)}{unit}
              {isWeight && <> · <span className="text-[#A3A3A3]">tara origen {fmt(sourceTara)} g</span></>}
            </p>
          </div>
          <button onClick={onClose} className="rounded p-1 text-[#737373] hover:bg-[#F5F5F5]">✕</button>
        </div>
        <div className="space-y-3 p-4">
          {err && <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-[#DC2626]">{err}</p>}
          <div className="wa-form-field">
            <label className="wa-label">Destino</label>
            <select value={toLoc} onChange={(e) => setToLoc(e.target.value)} className="wa-input">
              <option value="">—</option>
              {locations.filter((l) => l.id !== fromLoc.id).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </div>

          <div className="wa-form-field">
            <label className="wa-label">Cantidad ({unit})</label>
            <input type="number" step={step} min="0" value={byWeight ? (net > 0 ? net : '') : qty}
              onChange={(e) => setQty(e.target.value)} className="wa-input" placeholder="0" disabled={byWeight} />
          </div>

          {isWeight && (
            <div className="space-y-2 rounded-lg border border-[#E5E5E5] p-3">
              <p className="text-xs font-medium text-[#737373]">…o por peso del frasco</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="wa-form-field">
                  <label className="wa-label">Frasco en báscula (bruto, g)</label>
                  <input type="number" step={step} min="0" value={gross} onChange={(e) => setGross(e.target.value)} className="wa-input" placeholder="0.000" />
                </div>
                <div className="wa-form-field">
                  <label className="wa-label">Tara del frasco (g) · destino</label>
                  <input type="number" step={step} min="0" value={taraInput} onChange={(e) => { setTaraInput(e.target.value); setTaraTouched(true); }} className="wa-input" placeholder="0.000" />
                </div>
              </div>
              {gross !== '' && (
                <p className="rounded-md bg-lime-50 px-3 py-2 text-sm font-semibold text-lime-800">
                  Neto a traspasar: {net != null && !isNaN(net) ? `${fmt(net)} g` : '—'}
                </p>
              )}
            </div>
          )}

          <button onClick={submit} disabled={busy} className="w-full rounded-md bg-lime-500 py-2.5 text-sm font-semibold text-white hover:bg-lime-600 disabled:opacity-60">
            {busy ? 'Traspasando…' : 'Traspasar'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Diálogo crear ubicación ──
const AddLocationDialog = ({ onClose, onCreate }) => {
  const [name, setName] = useState('');
  const [kind, setKind] = useState('nevera');
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-sm rounded-2xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-[#E5E5E5] p-4">
          <h3 className="text-base font-semibold text-[#0A0A0A]">Nueva ubicación</h3>
          <button onClick={onClose} className="rounded p-1 text-[#737373] hover:bg-[#F5F5F5]">✕</button>
        </div>
        <div className="space-y-3 p-4">
          <div className="wa-form-field">
            <label className="wa-label">Nombre</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="wa-input" placeholder="Ej: Nevera barra" />
          </div>
          <div className="wa-form-field">
            <label className="wa-label">Tipo</label>
            <select value={kind} onChange={(e) => setKind(e.target.value)} className="wa-input">
              <option value="nevera">Nevera</option>
              <option value="armario">Armario</option>
              <option value="sub_almacen">Sub-almacén</option>
            </select>
          </div>
          <button onClick={() => name.trim() && onCreate(name.trim(), kind)} className="w-full rounded-md bg-lime-500 py-2.5 text-sm font-semibold text-white hover:bg-lime-600">Crear</button>
        </div>
      </div>
    </div>
  );
};

export default StockBoard;
